import React from "react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { useGetOnlineAssets } from "./hooks";
import PropTypes from "prop-types";
import { Table } from "semantic-ui-react";
import { CopyDataButton } from "./copy-data-button";
import { MetadataLinkButton } from "./metadata-link-button";

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

export const OnlineAssetList = ({ assetId, assetType }) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <OnlineAssetTable assetId={assetId} assetType={assetType} />
    </PersistQueryClientProvider>
  );
};

const OnlineAssetTable = ({ assetId, assetType }) => {
  const { data, isLoading, isError } = useGetOnlineAssets(assetId, assetType);

  if (isLoading || !data) {
    return null;
  }

  if (isError) {
    return <div>Error</div>;
  }

  return (
    <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Service type</Table.HeaderCell>
          <Table.HeaderCell>Service link</Table.HeaderCell>
          <Table.HeaderCell>Expected expiration date</Table.HeaderCell>
          <Table.HeaderCell>Metadata</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {data.length === 0 && (
          <Table.Row>
            <Table.Cell>No online assets found</Table.Cell>
          </Table.Row>
        )}

        {data.map((record) => {
          const { id, serviceType, onlineUrl, expectedLifetime, metadata } =
            record;
          return (
            <Table.Row key={id}>
              <Table.Cell>
                {[serviceType[0].toUpperCase(), serviceType.slice(1)].join("")}
              </Table.Cell>
              <Table.Cell>
                <a href={onlineUrl}>{onlineUrl}</a>
              </Table.Cell>
              <Table.Cell>
                {new Date(expectedLifetime).toLocaleString()}
              </Table.Cell>
              <Table.Cell>
                <CopyDataButton metadata={metadata} />
                <MetadataLinkButton link={`/api/oar/online-assets/${id}`} />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

OnlineAssetTable.propTypes = {
  assetId: PropTypes.string,
  assetType: PropTypes.string,
};

OnlineAssetList.propTypes = {
  assetId: PropTypes.string,
  assetType: PropTypes.string,
};

export default OnlineAssetList;
